import { Nullable } from "@core/domain/types/nullable.type";
import { BudgetDto, CategoryRowBudgetDto } from "@project/data/dto/budget.dto";
import { BudgetColumnMapper } from "@project/data/mappers/budget/columns/budget-column.mapper";
import {
    BudgetCategory,
    BudgetCategoryConcept,
    BudgetCategoryGroup,
    FinancialEntityBudget,
} from "@project/domain/models/budget/financial-entity-budget.model";
import { validateSync } from "class-validator";
import { inject, injectable } from "inversify";

@injectable()
export class FinancialEntityBudgetMapper {
    constructor(
        @inject(BudgetColumnMapper)
        private readonly budgetColumnMapper: BudgetColumnMapper,
    ) {}
    map(budgetDto: BudgetDto): Nullable<FinancialEntityBudget> {
        const errors = validateSync(budgetDto);

        if (errors.length) {
            console.error(errors);
            return null;
        }
        const columns = budgetDto.columns?.mapNotNull((column) =>
            this.budgetColumnMapper.map(column),
        );

        const categories = (budgetDto.categories ?? []).mapNotNull(
            (categoryDto: CategoryRowBudgetDto) => {
                // validate basic category fields already performed by parent validateSync
                const groups: BudgetCategoryGroup[] = (
                    categoryDto.groups ?? []
                ).mapNotNull((g) =>
                    g.id
                        ? new BudgetCategoryGroup(
                              g.id,
                              g.name,
                              g.category,
                              g.expense_type_mapping,
                              g.expense_subtype_mapping,
                          )
                        : null,
                );
                const concepts: BudgetCategoryConcept[] = (
                    categoryDto.concepts ?? []
                ).mapNotNull((c) =>
                    c.id
                        ? new BudgetCategoryConcept(
                              c.id,
                              c.name,
                              c.category,
                              c.group ?? undefined,
                          )
                        : null,
                );
                return new BudgetCategory(
                    categoryDto.id,
                    categoryDto.name,
                    categoryDto.budget,
                    groups,
                    concepts,
                );
            },
        );

        return new FinancialEntityBudget(
            budgetDto.id,
            budgetDto.name,
            budgetDto.description,
            budgetDto.finantial_entity,
            columns,
            categories,
            budgetDto.justification_template ?? undefined,
            budgetDto.includes_indirect_costs ?? undefined,
        );
    }
}
